export const deepDivePages = [
  {
    slug: 'about-leviathan',
    badge: 'FOUNDER PROFILE',
    cardIndex: '01',
    cardTitle: 'About Leviathan',
    cardSummary: 'A more personal look at the thinking, standards, and systems-first mindset behind the studio.',
    pageTitle: 'About Leviathan Coding Systems',
    metaTitle: 'About Leviathan Coding Systems | Marv Turner, Widnes Web Developer',
    metaDescription: 'Leviathan Coding Systems is a Widnes and North West based development studio founded by Marv Turner, focused on structured websites and digital systems that support real business growth.',
    metaKeywords: 'about Leviathan Coding Systems, Marv Turner, Widnes web developer, North West web development, bespoke digital systems',
    lead: 'Leviathan Coding Systems is a Widnes and North West based development studio built on a simple idea: businesses deserve better than rushed, generic digital solutions.',
    intro: 'I work directly with business owners to design and build websites and systems that not only look professional, but support how the business runs, attracts customers, and grows over time.',
    founderQuote: 'When systems fail in the real world, businesses feel it immediately. When they are built properly, operations run with far less friction.',
    sections: [
      {
        title: 'The Founder',
        paragraphs: [
          'Leviathan was founded by me, Marv Turner.',
          'My background was not a straight line into tech. I have worked across multiple industries, from mechanical work and retail management to personal training, physiotherapy, and telecoms engineering.',
          'It was in telecoms where things clicked. I worked on large-scale infrastructure projects, including building optical data frames for Openreach, leading teams, and implementing systems designed to perform under pressure.'
        ]
      },
      {
        title: 'Why Leviathan Exists',
        paragraphs: [
          'Over time, I kept seeing the same pattern. Hard-working businesses, especially smaller ones, were being held back by poor systems: outdated websites, manual processes, disconnected tools, and wasted time.',
          'Most of it was avoidable, but inside traditional roles you often hit a wall: office politics, outdated thinking, or resistance to change.',
          'Leviathan was built as a way out of that. A way to work directly with businesses that want to improve, modernise, and build something better.'
        ]
      },
      {
        title: 'How I Approach Building',
        paragraphs: [
          'I do not build for trends and I do not build for quick wins. I build systems that stay useful under real pressure, not just launch-day pressure.',
          'I am obsessive about quality. If something is not right, it gets fixed. If something can be improved, it gets improved.',
          'Most people have used a system and thought, "Why does this not just do what I need it to do?" That is exactly what I aim to solve.'
        ]
      },
      {
        title: 'Working With Businesses Across Widnes and the North West',
        paragraphs: [
          'I am based in Widnes and work with businesses across the North West.',
          'Where possible, I prefer direct conversations and structured sessions that surface what is actually needed.',
          'Not endless calls. Not long email chains. Just clear communication and focused execution.'
        ],
        links: [
          { label: 'How I help businesses grow', to: '/digital-growth' },
          { label: 'Supporting businesses in Widnes', to: '/web-developer-widnes' }
        ]
      }
    ],
    buildPrinciples: [
      'Clean and professional in design',
      'Fast, responsive, and reliable',
      'Structured to support real use',
      'Designed with long-term growth in mind'
    ],
    processSteps: [
      {
        title: 'Understand the Business',
        description: 'Before anything is built, I map how your business currently works, how customers move through it, and where friction is costing time or money.'
      },
      {
        title: 'Define the Right Solution',
        description: 'For some businesses that means a clean website. For others it means booking systems, dashboards, automation, or a structured combination.'
      },
      {
        title: 'Design and Build Properly',
        description: 'Everything is built around clarity, performance, clean architecture, and long-term maintainability. No shortcuts and no unnecessary complexity.'
      },
      {
        title: 'Refine and Launch',
        description: 'We review together, refine what needs refining, and only launch when the system works as intended.'
      },
      {
        title: 'Ongoing Support',
        description: 'After launch, I remain available for support and improvements as the business evolves, because good systems should evolve with you.'
      }
    ],
    expectations: [
      'A structured approach to building your digital presence',
      'Clear thinking without unnecessary technical complexity',
      'Systems designed to reduce friction and save time',
      'Someone who genuinely cares about real-world performance'
    ],
    vision: [
      'Leviathan is still early, but the direction is clear: grow into a high-end development studio built around standards, pride in execution, and meaningful long-term results.',
      'The ambition is not just to deliver projects. It is to build systems that are worth having and worth keeping.'
    ],
    closingCta: 'If you are looking for a developer who takes the time to understand your business and build something that genuinely supports it, let us talk.',
    relatedLinks: [
      { label: 'Web Developer Widnes', to: '/web-developer-widnes' },
      { label: 'Explore Digital Growth', to: '/digital-growth' },
      { label: 'Go to Contact', to: '/#contact' }
    ]
  },
  {
    slug: 'web-developer-widnes',
    badge: 'LOCAL SEO PAGE',
    cardIndex: '02',
    cardTitle: 'Web Developer Widnes',
    cardSummary: 'A location-focused page aimed at local businesses looking for a web developer with structure, clarity, and commercial awareness.',
    pageTitle: 'Web Developer in Widnes for Growing Businesses',
    metaTitle: 'Web Developer Widnes | Bespoke Websites & Systems for Local Businesses',
    metaDescription: 'Looking for a web developer in Widnes? Leviathan Coding Systems builds bespoke websites and digital systems for local businesses across Widnes and the North West.',
    metaKeywords: 'web developer Widnes, website design Widnes, bespoke websites Cheshire, local web developer North West, digital systems Widnes',
    lead: 'If you are a business based in Widnes and your website is not bringing in enquiries, feels outdated, or does not properly represent what you do, you are not alone.',
    intro: 'I work directly with local businesses to design and build websites and systems that do not just look good, but actually help the business run better, attract customers, and grow over time.',
    founderQuote: 'A good digital setup should remove friction, not create it. The right system should help your business move faster, not make simple tasks harder.',
    secondaryCta: {
      label: 'View Services',
      to: '/#services'
    },
    sections: [
      {
        title: 'A Local Developer You Can Actually Work With',
        paragraphs: [
          'Leviathan Coding Systems is based in Widnes and works with businesses across the surrounding area.',
          'This is not a faceless agency or an outsourced service. It is direct, local, and personal.',
          'Where possible, I prefer to sit down with business owners, understand how things currently work, and build something that genuinely fits rather than forcing you into a template or generic system.'
        ]
      },
      {
        title: 'The Problems I See Every Day',
        paragraphs: [
          'A lot of local businesses are not struggling because they lack effort. They are struggling because their digital setup is holding them back.',
          'These problems do not fix themselves, but they are fixable.'
        ],
        points: [
          'Websites that look outdated or fail to reflect the quality of the business',
          'No clear path for customers to get in touch or book',
          'Manual processes that waste time',
          'Systems that do not connect properly',
          'No clear structure for how the website supports growth'
        ]
      },
      {
        title: 'What I Build',
        paragraphs: [
          'I design and build digital systems that support how your business actually operates.',
          'That can include clean websites that convert, booking and scheduling systems, client dashboards, automation, and integrations between tools you already use.',
          'The goal is always the same: make things clearer, faster, and easier to manage.'
        ]
      },
      {
        title: 'Built Properly, Not Rushed',
        paragraphs: [
          'Most businesses have experienced systems that feel incomplete or frustrating to use. That usually comes from rushed builds and cut corners.',
          'I take a different approach: build carefully, test properly, and refine until it performs as intended.',
          'If something is not right, it gets fixed. If it can be improved, it gets improved.'
        ]
      },
      {
        title: 'Supporting Businesses in Widnes and Beyond',
        paragraphs: [
          'While I am based in Widnes, I work with businesses across Cheshire and the wider North West.',
          'Whether you need a clean website to establish your presence or a more advanced system to support operations, the principle is the same: understand the business first, then build the right solution.'
        ],
        links: [
          { label: 'How I help businesses grow', to: '/digital-growth' },
          { label: 'Learn more about how I work', to: '/about-leviathan' }
        ]
      }
    ],
    buildPrinciples: [
      'Professional design that builds immediate credibility',
      'Fast, responsive performance across mobile and desktop',
      'Clear customer journeys that support conversion',
      'Clean architecture that stays maintainable over time'
    ],
    processSteps: [
      {
        title: 'Local Discovery',
        description: 'We start by mapping how your business gets customers, where friction is happening, and what outcomes matter most.'
      },
      {
        title: 'Practical Scope',
        description: 'I define a focused solution that solves current pain points first, without adding complexity for the sake of it.'
      },
      {
        title: 'Design and Build',
        description: 'Your site or system is built with conversion clarity, technical reliability, and real-world usability in mind.'
      },
      {
        title: 'Review and Refine',
        description: 'We review together, tighten where needed, and make sure it works in real use before launch.'
      },
      {
        title: 'Ongoing Support',
        description: 'After launch, I remain available for updates, improvements, and next-stage development as your business evolves.'
      }
    ],
    expectations: [
      'Direct communication with the person building the work',
      'Structured decisions grounded in business outcomes',
      'A website or system that is easier to use and easier to manage',
      'A long-term approach focused on reliability and growth'
    ],
    vision: [
      'The aim is to become the go-to development studio for ambitious businesses in Widnes and the wider region who want systems built properly from the start.',
      'Not just another launch. A stronger digital foundation that continues to pay off as the business grows.'
    ],
    closingCta: 'If you are based in Widnes and looking for a developer who will take the time to understand your business and build something that genuinely supports it, let us have a conversation.',
    relatedLinks: [
      { label: 'Read About Leviathan', to: '/about-leviathan' },
      { label: 'Explore Digital Growth', to: '/digital-growth' },
      { label: 'Go to Contact', to: '/#contact' }
    ]
  },
  {
    slug: 'digital-growth',
    badge: 'STRATEGY PAGE',
    cardIndex: '03',
    cardTitle: 'Digital Growth',
    cardSummary: 'An in-depth page explaining how digital systems, websites, and operational structure support business growth over time.',
    pageTitle: 'Digital Growth Through Better Systems',
    metaTitle: 'Digital Growth for Small Businesses | Websites, Systems & SEO Foundations',
    metaDescription: 'Digital growth through better systems: websites, SEO foundations, conversion structure, automation, and maintainable infrastructure built around your business.',
    metaKeywords: 'digital growth for small business, SEO foundations, conversion-focused website, business automation UK, scalable digital systems',
    lead: 'A website on its own does not grow a business.',
    intro: 'It is how that website is structured, how it captures attention, and how it supports your day-to-day operations that makes the difference. At Leviathan Coding Systems, the focus is not just building something that looks good, but creating digital systems that help your business attract customers, convert enquiries, and operate more efficiently.',
    founderQuote: 'Real growth does not come from quick fixes. It comes from building the right system, then improving it with intent.',
    secondaryCta: {
      label: 'Start a Project',
      to: '/#contact'
    },
    relatedLinks: [
      { label: 'Read About Leviathan', to: '/about-leviathan' },
      { label: 'Web Developer Widnes', to: '/web-developer-widnes' },
      { label: 'Go to Contact', to: '/#contact' }
    ],
    sections: [
      {
        title: 'It Is Not Just a Website',
        paragraphs: [
          'A lot of businesses invest in a website expecting results, but nothing really changes. That is usually because the website is not part of a bigger system.',
          'Without a clear customer journey, a defined enquiry process, and a connection to how the business actually runs, even a good-looking site will struggle to deliver value.'
        ]
      },
      {
        title: 'How Digital Growth Actually Happens',
        paragraphs: [
          'Growth comes from small, well-structured improvements working together. It is not about chasing trends. It is about building something that works properly.'
        ],
        points: [
          'A clear, professional online presence',
          'A simple path for customers to take action',
          'Systems that capture and manage enquiries',
          'Reduced friction in day-to-day operations',
          'Visibility through SEO foundations'
        ]
      },
      {
        title: 'Built With SEO in Mind',
        paragraphs: [
          'Every build includes the foundations needed for your business to be visible online. SEO is not about gaming the system. It is about helping the right people find you.'
        ],
        points: [
          'Clean structure that search engines can understand',
          'Proper page hierarchy and content organisation',
          'Fast load speeds and mobile optimisation',
          'Pages aligned to real search intent'
        ]
      },
      {
        title: 'Designed Around Your Business',
        paragraphs: [
          'No two businesses operate the same way. That is why everything is built around how you actually work rather than forced into a generic template.',
          'For some, that means a clean website that generates enquiries. For others, it means booking systems, automation, or dashboards that support daily operations.',
          'The goal is always the same: build something that genuinely helps your business move forward.'
        ],
        links: [
          { label: 'Work with local businesses in Widnes', to: '/web-developer-widnes' },
          { label: 'About Leviathan', to: '/about-leviathan' }
        ]
      },
      {
        title: 'Long-Term Value',
        paragraphs: [
          'The focus is not short-term wins. It is on building something that continues to deliver value over time.',
          'Because real growth does not come from quick fixes. It comes from building properly.'
        ],
        points: [
          'Systems that do not need constant fixing',
          'Websites that remain relevant and adaptable',
          'A structure that supports future growth'
        ]
      }
    ],
    buildPrinciples: [
      'Clarity: communicate what you do and who it is for',
      'Conversion: turn visitors into enquiries with clear next steps',
      'Efficiency: reduce manual work with better systems and automation',
      'Performance: fast, responsive, and reliable across devices',
      'Structure: built to evolve as the business grows'
    ],
    processSteps: [
      {
        title: 'Understand the Current Setup',
        description: 'Map what is already working, where friction exists, and where growth is currently being lost.'
      },
      {
        title: 'Define the Right Priorities',
        description: 'Prioritise the highest-impact improvements first so progress is practical and measurable.'
      },
      {
        title: 'Build and Integrate',
        description: 'Implement website and system improvements with clean structure and real operational usability.'
      },
      {
        title: 'Review and Refine',
        description: 'Measure what matters, tighten weak spots, and improve continuously based on real outcomes.'
      },
      {
        title: 'Support Ongoing Growth',
        description: 'Evolve the system as the business grows so progress stays consistent and manageable.'
      }
    ],
    expectations: [
      'Clearer positioning and better quality enquiries',
      'Less friction across your customer journey and internal workflow',
      'Stronger digital foundations for SEO and conversion',
      'A system that remains dependable as demand increases'
    ],
    vision: [
      'Digital growth should feel structured, not overwhelming. When the foundations are right, improvement becomes easier and more consistent over time.',
      'The objective is simple: build digital systems that help your business scale without adding unnecessary complexity.'
    ],
    closingCta: 'If you are looking to improve how your business operates online, whether that is generating more enquiries, streamlining processes, or building something more structured, we can start with a conversation.'
  }
];

export const deepDivePageMap = Object.fromEntries(
  deepDivePages.map((page) => [page.slug, page])
);